import { Check, Translate, Undo } from "@mui/icons-material";
import { Alert, Autocomplete, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Tooltip } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RecordManagement } from "app/class/record-management";
import { translationResolveQueryKey, useTranslationResolver } from "app/hooks/useTranslationResolver";
import { useCanManageTranslations } from "app/hooks/useCanManageTranslations";

export const isTranslationKey = (candidate: string) => /^[a-z][a-z0-9_-]*(\.[a-z0-9_-]+)+$/i.test(String(candidate || "").trim());

interface TranslatableInputProps {
    label: string;
    value: string;
    suggestedKey: string;
    /** Display fallback for a missing locale translation; the stored value stays the key. */
    fallbackText?: string;
    multiline?: boolean;
    helperText?: string;
    defaultPublic?: boolean;
    disabled?: boolean;
    /**
     * O Record Producer já renderiza o label do campo via `BaseField` (fora deste
     * componente). Passe `true` nesses contextos para não duplicar o label dentro
     * do TextField — `label` continua usado para o fallback de exibição e o tooltip.
     */
    hideLabel?: boolean;
    /** Estilo de erro vindo de fora (ex.: validação do formulário) -- combinado com o erro interno. */
    invalid?: boolean;
    /**
     * Chamado sempre que uma edição fica pendente de confirmação (ver botões de check/desfazer)
     * ou deixa de estar pendente. Contextos com formulário (record-producer) usam isso para marcar
     * o campo como invalido/dirty enquanto a tradução não é confirmada, evitando que o usuário
     * salve o registro e perca a edição digitada sem querer.
     */
    onDirtyChange?: (isDirty: boolean) => void;
    onDraftChange?: (draft: string) => void;
    inputRef?: React.Ref<HTMLInputElement>;
    showTranslationKey?: boolean;
    /**
     * Chamado depois que uma tradução é confirmada (botão de check, Enter, ou o dialog) e a key
     * já foi propagada via `onChange`. O valor em si já foi persistido em `sys_translations` nesse
     * ponto -- isso é só para o chamador (ex.: o campo do record-producer) opcionalmente persistir
     * a key no próprio registro imediatamente, sem esperar o usuário salvar o formulário inteiro.
     * Erros lançados aqui são tratados como falha da confirmação (mostrados via helperText).
     */
    onConfirmed?: (key: string) => void | Promise<void>;
    registerFlush?: (flush: () => Promise<boolean>) => () => void;
    translationKeyOverride?: string;
    commitMode?: "record";
    onChange: (value: string, displayValue?: string) => void;
}

interface TranslationRecord {
    id: string;
    language: string;
    text_key: string;
    text_value: string;
    public?: boolean | null;
}

interface LanguageOption {
    id: string;
    label: string;
    value: string;
    active?: boolean | null;
    order?: number | null;
}

const findTranslation = (language: string, textKey: string) =>
    new RecordManagement<TranslationRecord>("sys_translations")
        .where("language", language)
        .where("text_key", textKey)
        .first();

const TranslatableInput = ({ label, value, suggestedKey, fallbackText, multiline = false, helperText, defaultPublic, disabled = false, hideLabel = false, invalid = false, onDirtyChange, onDraftChange, inputRef, showTranslationKey = true, onConfirmed, registerFlush, translationKeyOverride, commitMode, onChange }: TranslatableInputProps) => {
    const { t, i18n } = useTranslation();
    const queryClient = useQueryClient();
    const canManageTranslations = useCanManageTranslations();
    const currentLanguage = i18n.resolvedLanguage || i18n.language || "pt-BR";
    const languageOptions = useQuery({
        queryKey: ["sys_languages", "active"],
        queryFn: async () => {
            const response = await new RecordManagement<LanguageOption>("sys_languages")
                .where("active", true)
                .orderBy("order")
                .run();

            return response.data ?? [];
        },
    });

    const rawRecordKey = commitMode === "record" && isTranslationKey(value);
    const translationKey = translationKeyOverride || (isTranslationKey(value) ? value : "");

    // `committedValue` is the last known-good text for (translationKey, currentLanguage) -- either
    // resolved from the backend or confirmed locally. `draftValue` is what the TextField shows/edits.
    // They're only allowed to diverge while the user has an unconfirmed inline edit pending.
    const [committedValue, setCommittedValue] = useState<string>(() => (translationKey ? fallbackText || "" : (value ?? "")));
    const [draftValue, setDraftValue] = useState<string>(committedValue);
    const [savingInline, setSavingInline] = useState(false);
    const [inlineError, setInlineError] = useState<string | null>(null);
    const lastAppliedValueRef = useRef(value);

    const [open, setOpen] = useState(false);
    const [keyValue, setKeyValue] = useState(suggestedKey);
    const [language, setLanguage] = useState(currentLanguage);
    const [textValue, setTextValue] = useState(value && !value.includes(".") ? value : "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const displayResolver = useTranslationResolver(translationKey && (commitMode !== "record" || rawRecordKey) ? [translationKey] : [], currentLanguage, Boolean(translationKey && (commitMode !== "record" || rawRecordKey)));
    const dialogResolver = useTranslationResolver(open && keyValue ? [keyValue] : [], language, open && Boolean(keyValue));
    const loadingDisplayTranslation = translationKey ? displayResolver.isFetching : false;
    const loadingTranslation = open && keyValue ? dialogResolver.isFetching : false;
    const isDirty = draftValue !== committedValue;

    const onDirtyChangeRef = useRef(onDirtyChange);
    onDirtyChangeRef.current = onDirtyChange;
    const isDirtyRef = useRef(isDirty);
    isDirtyRef.current = isDirty;
    const draftValueRef = useRef(draftValue);
    draftValueRef.current = draftValue;
    const savingPromiseRef = useRef<Promise<boolean> | null>(null);
    const confirmDraftRef = useRef<() => Promise<boolean>>(async () => true);

    useEffect(() => commitMode === "record" ? undefined : registerFlush?.(async () => {
        if (savingPromiseRef.current) return savingPromiseRef.current;
        if (!isDirtyRef.current) return true;
        return confirmDraftRef.current();
    }), [registerFlush, commitMode]);

    useEffect(() => {
        onDirtyChangeRef.current?.(isDirty);
    }, [isDirty]);

    // If the field unmounts while an edit is still unconfirmed (e.g. it becomes hidden), clear
    // whatever pending flag was raised instead of leaving the form stuck with a stale warning.
    useEffect(() => {
        return () => {
            if (isDirtyRef.current) {
                onDirtyChangeRef.current?.(false);
            }
        };
    }, []);

    // The record's own field value changed from outside this component (a different record/field
    // was loaded) -- resync. This must NOT fire for changes this component caused itself (tracked
    // via lastAppliedValueRef), or a just-confirmed edit would immediately get wiped.
    useEffect(() => {
        if (lastAppliedValueRef.current === value) return;
        lastAppliedValueRef.current = value;
        const next = isTranslationKey(value) ? fallbackText || "" : (value ?? "");
        setCommittedValue(next);
        setDraftValue(next);
        onDraftChange?.(next);
        draftValueRef.current = next;
        isDirtyRef.current = false;
        setInlineError(null);
    }, [value, fallbackText]);

    // Adopt a resolved translation for the current language, but only when there's no unconfirmed
    // local edit in progress, and only when something was actually found -- if the current language
    // has no translation for this key yet, the field keeps showing whatever it already displayed
    // (last language's text, or blank on first load) instead of blanking out or leaking another
    // language's text.
    useEffect(() => {
        if (!translationKey || (commitMode === "record" && !rawRecordKey)) return;
        const resolved = displayResolver.data?.[translationKey] || fallbackText;
        if (!resolved || resolved === committedValue || draftValue !== committedValue) return;
        setCommittedValue(resolved);
        setDraftValue(resolved);
    }, [displayResolver.data, translationKey, committedValue, draftValue, fallbackText, commitMode, rawRecordKey]);

    useEffect(() => {
        if (!open || !keyValue || !language) return;

        const resolved = dialogResolver.data?.[keyValue];
        setTextValue(resolved || (translationKey ? "" : value));
        setError(null);
        }, [dialogResolver.data, keyValue, language, open, translationKey, value]);

    const openDialog = () => {
        setKeyValue(translationKey || suggestedKey);
        setTextValue("");
        setLanguage(currentLanguage);
        setError(null);
        setOpen(true);
    };

    const addRuntimeTranslation = (targetLanguage: string, textKey: string, translatedText: string) => {
        if (!targetLanguage || !textKey) return;
        // Only refresh the active locale's runtime bundle. i18next's `fallbackLng` means
        // adding a resource for any other language leaks through `t()` for users on a
        // locale that has no translation yet, showing the wrong language instead of nothing.
        if (targetLanguage !== currentLanguage) return;
        try {
            i18n.addResource(targetLanguage, "translation", textKey, translatedText, { silent: true });
        } catch (_error) {
            // Runtime i18n refresh is best-effort; persistence remains the source of truth.
        }
    };

    const buildPayload = (targetLanguage: string, textKey: string, translatedText: string): Partial<TranslationRecord> => {
        const payload: Partial<TranslationRecord> = {
            language: targetLanguage,
            text_key: textKey,
            text_value: translatedText,
        };

        if (typeof defaultPublic === "boolean") {
            payload.public = defaultPublic;
        }

        return payload;
    };

    const chooseKey = async (candidate: string, values: Array<{ language: string; text: string }>): Promise<string> => {
        if (translationKey === candidate) return candidate;
        let key = candidate;
        for (let attempt = 0; attempt < 10; attempt += 1) {
            const conflicts = await Promise.all(values.map(async ({ language, text }) => {
                const existing = await findTranslation(language, key);
                return existing?.id && existing.text_value !== text;
            }));
            if (!conflicts.some(Boolean)) return key;
            key = `${candidate}.${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
        }
        throw new Error(String(t("Unable to find a free translation key.")));
    };

    const invalidateResolvedTranslation = async (targetLanguage: string, textKey: string) => {
        await queryClient.invalidateQueries({ queryKey: translationResolveQueryKey(targetLanguage, [textKey]) });
    };

    const upsertTranslation = async (manager: RecordManagement<TranslationRecord>, targetLanguage: string, textKey: string, translatedText: string) => {
        const payload = buildPayload(targetLanguage, textKey, translatedText);
        const existing = await findTranslation(targetLanguage, textKey);

        if (existing?.id) {
            if (translationKey !== textKey && existing.text_value !== translatedText) {
                throw new Error(String(t("Translation key is already in use.")));
            }
            await manager.update(existing.id, payload);
        } else {
            try {
                await manager.save(payload);
            } catch (error) {
                const conflicting = await findTranslation(targetLanguage, textKey);
                if (!conflicting?.id) throw error;
                if (translationKey !== textKey && conflicting.text_value !== translatedText) throw error;
                await manager.update(conflicting.id, payload);
            }
        }

        addRuntimeTranslation(targetLanguage, textKey, translatedText);
        // Write the known-correct value straight into the cache so the display effect above never
        // sees a stale ("not found yet") response for a key we just saved -- invalidateQueries alone
        // leaves a window where the old cached data is still served until the refetch lands.
        queryClient.setQueryData(translationResolveQueryKey(targetLanguage, [textKey]), (old: Record<string, string> | undefined) => ({
            ...(old || {}),
            [textKey]: translatedText,
        }));
        await invalidateResolvedTranslation(targetLanguage, textKey);
    };

    const saveTranslation = async () => {
        setSaving(true);
        setError(null);
        try {
            const manager = new RecordManagement<TranslationRecord>("sys_translations");
            const originalText = !translationKey ? String(value || "").trim() : "";
            // Only the current language's resolved text is safe to show in the field below.
            // Saving a translation for a *different* language must never change what the
            // logged-in user's own language displays.
            let currentLanguageText: string | undefined;

            const key = await chooseKey(keyValue, [
                { language, text: textValue },
                ...(originalText && currentLanguage !== language ? [{ language: currentLanguage, text: originalText }] : []),
            ]);
            if (originalText && currentLanguage && currentLanguage !== language) {
                await upsertTranslation(manager, currentLanguage, key, originalText);
                currentLanguageText = originalText;
            }

            await upsertTranslation(manager, language, key, textValue);

            if (language === currentLanguage) {
                currentLanguageText = textValue;
            }

            lastAppliedValueRef.current = key;
            if (currentLanguageText !== undefined) {
                setCommittedValue(currentLanguageText);
                setDraftValue(currentLanguageText);
            }
            onChange(commitMode === "record" ? (currentLanguageText ?? value) : key, currentLanguageText);
            await onConfirmed?.(key);
            setOpen(false);
        } catch (err: any) {
            setError(err?.response?.data?.error?.message || err?.message || String(t("Unable to save translation.")));
        } finally {
            setSaving(false);
        }
    };

    const cancelDraft = () => {
        setDraftValue(committedValue);
        onDraftChange?.(committedValue);
        draftValueRef.current = committedValue;
        isDirtyRef.current = false;
        setInlineError(null);
    };

    const confirmDraft = async (): Promise<boolean> => {
        if (savingPromiseRef.current) return savingPromiseRef.current;
        if (!isDirtyRef.current) return true;
        const operation = (async () => {
            setSavingInline(true);
            setInlineError(null);
            try {
                const latestDraft = draftValueRef.current;
                if (!translationKey && !latestDraft.trim()) {
                    lastAppliedValueRef.current = latestDraft;
                    setCommittedValue(latestDraft);
                    isDirtyRef.current = false;
                    onChange(latestDraft);
                    return true;
                }

                const key = await chooseKey(translationKey || suggestedKey, [{ language: currentLanguage, text: latestDraft }]);
                const manager = new RecordManagement<TranslationRecord>("sys_translations");
                await upsertTranslation(manager, currentLanguage, key, latestDraft);

                lastAppliedValueRef.current = key;
                setCommittedValue(latestDraft);
                isDirtyRef.current = false;
                onChange(key, latestDraft);
                await onConfirmed?.(key);
                return true;
            } catch (err: any) {
                setInlineError(err?.response?.data?.error?.message || err?.message || String(t("Unable to save translation.")));
                return false;
            } finally {
                setSavingInline(false);
            }
        })();
        savingPromiseRef.current = operation;
        try {
            return await operation;
        } finally {
            savingPromiseRef.current = null;
        }
    };
    confirmDraftRef.current = confirmDraft;

    const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const next = event.target.value;

        if (commitMode === "record" || !canManageTranslations) {
            // Same behavior as before this field type existed: a plain, ungated literal edit.
            lastAppliedValueRef.current = next;
            setCommittedValue(next);
            setDraftValue(next);
            draftValueRef.current = next;
            isDirtyRef.current = false;
            onDraftChange?.(next);
            onChange(next);
            return;
        }

        setDraftValue(next);
        onDraftChange?.(next);
        draftValueRef.current = next;
        isDirtyRef.current = next !== committedValue;
        setInlineError(null);
    };

    const handleFieldKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!canManageTranslations || commitMode === "record") return;

        if (event.key === "Escape" && isDirty) {
            event.preventDefault();
            cancelDraft();
            return;
        }

        // In multiline fields Enter must keep inserting newlines; only the button confirms there.
        if (event.key === "Enter" && !multiline && !event.shiftKey && isDirty) {
            event.preventDefault();
            confirmDraft();
        }
    };

    return (
        <>
            <TextField
                size="small"
                inputRef={inputRef}
                inputProps={{ title: draftValue }}
                label={hideLabel ? undefined : t(label)}
                value={draftValue}
                onChange={handleFieldChange}
                onKeyDown={handleFieldKeyDown}
                disabled={disabled}
                error={Boolean(inlineError) || invalid}
                helperText={inlineError || (helperText ? t(helperText) : undefined)}
                multiline={multiline}
                minRows={multiline ? 2 : undefined}
                fullWidth
                InputProps={{
                    endAdornment: (
                        <>
                            {(loadingDisplayTranslation || savingInline) && <CircularProgress size={16} sx={{ mr: 0.5 }} />}
                            {canManageTranslations && commitMode !== "record" && isDirty && !savingInline && (
                                <>
                                    <Tooltip title={t("Confirm translation")}>
                                        <IconButton size="small" edge="end" onClick={confirmDraft} disabled={disabled}>
                                            <Check fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t("Discard changes")}>
                                        <IconButton size="small" edge="end" onClick={cancelDraft} disabled={disabled}>
                                            <Undo fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            )}
                            {canManageTranslations && (
                                <Tooltip title={t("Create or update translation")}>
                                    <IconButton size="small" edge="end" onClick={openDialog} disabled={disabled}>
                                        <Translate fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </>
                    ),
                }}
            />
            <Dialog
                open={open}
                onClose={(_, reason) => {
                    if (reason === "backdropClick") return;
                    setOpen(false);
                }}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>{t("Translation")}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        {error && <Alert severity="error">{error}</Alert>}
                        {showTranslationKey && <TextField size="small" label={t("Key")} value={keyValue} onChange={(event) => setKeyValue(event.target.value)} fullWidth />}
                        <Autocomplete
                            size="small"
                            options={languageOptions.data ?? []}
                            value={(languageOptions.data ?? []).find((option) => option.value === language) ?? null}
                            loading={languageOptions.isLoading}
                            onChange={(_, option) => setLanguage(option?.value || "")}
                            getOptionLabel={(option) => option.label || option.value}
                            isOptionEqualToValue={(option, selected) => option.value === selected.value}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t("Language")}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {languageOptions.isLoading && <CircularProgress size={18} />}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                        <TextField
                            size="small"
                            label={t("Text")}
                            value={textValue}
                            onChange={(event) => setTextValue(event.target.value)}
                            helperText={loadingTranslation ? t("Loading translation...") : undefined}
                            multiline
                            minRows={3}
                            fullWidth
                            disabled={loadingTranslation}
                            InputProps={{
                                endAdornment: loadingTranslation ? <CircularProgress size={18} /> : undefined,
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>{t("Cancel")}</Button>
                    <Button disabled={saving || loadingTranslation || !keyValue || !language} variant="contained" onClick={saveTranslation}>{saving ? t("Saving...") : t("Save translation")}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default TranslatableInput;
